import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Edit, Trash2 } from "lucide-react";
import {
  Button,
  Badge,
  Switch,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@b2b/ui-components";
import { BaseColumnDef } from "@b2b/ui-components/components/Table/types";

// Category interface - matches the one in AdminCategories.tsx
export interface Category {
  id: string;
  name: string;
  type?: string;
  description?: string;
  slug: string;
  parentId?: string;
  isActive: boolean;
  sortOrder: number;
  featured: boolean;
  banner?: string;
  icon?: string;
  coverImage?: string;
  metaTitle?: string;
  metaDescription?: string;
  filteringAttributes?: string[];
  createdAt: string;
  updatedAt: string;
  parent?: Category;
  children?: Category[];
  productCount: number;
  level?: number;
}

interface CreateCategoryColumnsOptions {
  onEdit: (category: Category) => void;
  onToggleActive: (id: string) => void;
  onToggleFeatured: (id: string) => void;
  onDelete: (id: string) => void;
}

const calculateLevel = (cat: Category, depth = 0): number => {
  if (!cat.parent) return depth;
  return calculateLevel(cat.parent, depth + 1);
};

/** Small list thumbnail; link opens full asset in a new tab. */
function CategoryImagePreview({
  url,
  variant,
}: {
  url?: string;
  variant: "banner" | "cover" | "icon";
}) {
  const [broken, setBroken] = useState(false);
  const trimmed = url?.trim();
  if (!trimmed) {
    return <span className="text-gray-400 text-xs">—</span>;
  }
  if (broken) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const box =
    variant === "banner"
      ? "h-10 w-[4.5rem] min-w-[3.5rem]"
      : variant === "icon"
        ? "h-9 w-9 min-w-[2.25rem] rounded-md"
        : "h-10 w-10 min-w-[2.5rem]";
  return (
    <a
      href={trimmed}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex overflow-hidden rounded-md border border-gray-200 bg-gray-50 ${box} items-center justify-center hover:ring-2 hover:ring-[#FF6A00]/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6A00]`}
      title="Open full image"
    >
      <img
        src={trimmed}
        alt=""
        className={`max-h-full max-w-full object-cover ${box}`}
        loading="lazy"
        onError={() => setBroken(true)}
      />
    </a>
  );
}

export function createCategoryColumns(
  options: CreateCategoryColumnsOptions
): ColumnDef<Category>[] {
  const { onEdit, onToggleActive, onToggleFeatured, onDelete } = options;

  const columns: ColumnDef<Category>[] = [
    {
      id: "index",
      header: "S.NO",
      size: 56,
      minSize: 48,
      maxSize: 64,
      headerClassName: "!px-2",
      cellClassName: "!px-2",
      cell: ({ row, table }) => {
        // `row.index` is the index in the full sorted dataset, not the page (adding pageIndex*pageSize double-counts).
        const sortedRows = table.getSortedRowModel().rows;
        const pos = sortedRows.findIndex((r) => r.id === row.id);
        const serial = pos >= 0 ? pos + 1 : row.index + 1;
        return <span className="tabular-nums text-center block">{serial}</span>;
      },
      enableSorting: false,
      align: "center",
    } as BaseColumnDef<Category>,
    {
      accessorKey: "name",
      header: "Name",
      size: 220,
      minSize: 180,
      maxSize: 260,
      headerClassName: "!px-3",
      cellClassName: "!px-3",
      cell: ({ row }) => {
        const category = row.original;
        return (
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <span className="font-medium text-foreground truncate">{category.name}</span>
            {category.type ? (
              <Badge variant="outline" className="text-xs shrink-0">
                {category.type}
              </Badge>
            ) : null}
          </div>
        );
      },
      enableSorting: true,
      align: "left",
    } as BaseColumnDef<Category>,
    {
      accessorKey: "icon",
      header: "Icon",
      cell: ({ row }) => (
        <div className="flex justify-center">
          <CategoryImagePreview url={row.original.icon} variant="icon" />
        </div>
      ),
      enableSorting: false,
      align: "center",
    } as BaseColumnDef<Category>,
    {
      accessorKey: "banner",
      header: "Banner",
      cell: ({ row }) => (
        <div className="flex justify-center">
          <CategoryImagePreview url={row.original.banner} variant="banner" />
        </div>
      ),
      enableSorting: false,
      align: "center",
    } as BaseColumnDef<Category>,
    {
      accessorKey: "coverImage",
      header: "Cover",
      cell: ({ row }) => (
        <div className="flex justify-center">
          <CategoryImagePreview url={row.original.coverImage} variant="cover" />
        </div>
      ),
      enableSorting: false,
      align: "center",
    } as BaseColumnDef<Category>,
    {
      accessorKey: "parent",
      header: "Parent",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.parent?.name || "—"}
        </span>
      ),
      enableSorting: true,
      align: "left",
    } as BaseColumnDef<Category>,
    {
      accessorKey: "sortOrder",
      header: "Sort",
      cell: ({ row }) => (
        <span className="tabular-nums text-center block">{row.original.sortOrder}</span>
      ),
      enableSorting: true,
      align: "center",
    } as BaseColumnDef<Category>,
    {
      id: "level",
      header: "Level",
      cell: ({ row }) => {
        const category = row.original;
        const level =
          typeof category.level === "number" ? category.level : calculateLevel(category);
        return <span className="tabular-nums text-center block">{level}</span>;
      },
      enableSorting: false,
      align: "center",
    } as BaseColumnDef<Category>,
    {
      accessorKey: "productCount",
      header: "Products",
      cell: ({ row }) => (
        <span className="tabular-nums text-center block">
          {row.original.productCount ?? 0}
        </span>
      ),
      enableSorting: true,
      align: "center",
    } as BaseColumnDef<Category>,
    {
      id: "isActive",
      header: "Active",
      size: 86,
      minSize: 78,
      maxSize: 92,
      headerClassName: "!px-2",
      cellClassName: "!px-2",
      cell: ({ row }) => {
        const category = row.original;
        return (
          <div className="flex justify-center">
            <Switch
              checked={category.isActive}
              onCheckedChange={() => onToggleActive(category.id)}
              aria-label={category.isActive ? "Deactivate category" : "Activate category"}
            />
          </div>
        );
      },
      enableSorting: false,
      align: "center",
    } as BaseColumnDef<Category>,
    {
      id: "featured",
      header: "Featured",
      size: 92,
      minSize: 84,
      maxSize: 98,
      headerClassName: "!px-2",
      cellClassName: "!px-2",
      cell: ({ row }) => {
        const category = row.original;
        return (
          <div className="flex justify-center">
            <Switch
              checked={category.featured}
              onCheckedChange={() => onToggleFeatured(category.id)}
              aria-label={category.featured ? "Remove featured" : "Mark featured"}
            />
          </div>
        );
      },
      enableSorting: false,
      align: "center",
    } as BaseColumnDef<Category>,
    {
      id: "actions",
      header: "Actions",
      size: 96,
      minSize: 92,
      maxSize: 108,
      headerClassName: "!px-2",
      cellClassName: "!px-2",
      cell: ({ row }) => {
        const category = row.original;
        return (
          <div className="flex items-center justify-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => onEdit(category)}
              aria-label={`Edit ${category.name}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-destructive hover:text-destructive"
                  aria-label={`Delete ${category.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete category</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete &quot;{category.name}&quot;? This
                    cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(category.id)}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        );
      },
      enableSorting: false,
      align: "center",
    } as BaseColumnDef<Category>,
  ];

  return columns;
}
